/**
 * Scorecard Cells Delta - Alpine.js Mixin
 *
 * Efficiently computes and displays period-over-period delta percentages
 * Uses header-index-based previous period lookup and aggressive caching
 *
 * Each pill carries two independent axes:
 * - direction (up / down / flat): the raw sign of the change — drives the arrow and the +/- sign.
 * - tone (good / bad / neutral): whether that direction is good for the metric's goal direction —
 *   drives the pill colour. So a lower-is-better metric that fell shows a down arrow in green,
 *   matching the metric drawer's header delta.
 */

import { metricHigherIsBetter, DELTA_TONE } from '../utils';

export default (options = {}) => ({
    deltaCache: {},

    // Translated by Blade at the call site (this module has no translator). The English fallbacks
    // keep the cue present if the mixin is ever spread without options.
    deltaToneLabels: {
        good: options.toneLabels?.good ?? 'improving',
        bad: options.toneLabels?.bad ?? 'declining',
    },

    /**
     * Check if delta view should be shown
     */
    shouldShowDelta() {
        if (!this.activeTab) return false;
        const visualIndicator = this.activeTab?.settings?.visual_indicator || 'color';
        return visualIndicator === 'delta';
    },

    calculateDelta(current, previous) {
      const curr = Number(current) || 0;
      const prev = Number(previous) || 0;

      const absolute = curr - prev;

      let percent;

      if (prev === 0) {
        if (curr === 0) {
          percent = 0;
        } else {
          percent = 100; 
        }
      } else {
        percent = (absolute / Math.abs(prev)) * 100;
      }

      return percent;
    },

    /**
     * Turn a signed delta percent into a pill descriptor: its direction (arrow + sign) and its tone
     * (colour). Tone respects the metric's goal direction, so a decrease reads as good for a
     * lower-is-better metric. Shared by getDeltaInfo and getProjectedDeltaInfo.
     *
     * @param {number} deltaPercent
     * @param {Object} metric - the metric record (for goal_criteria)
     * @returns {{ percent: string, direction: 'up'|'down'|'flat', tone: 'good'|'bad'|'neutral', sign: '+'|'-' }}
     */
    buildDeltaResult(deltaPercent, metric) {
        const higherIsBetter = metricHigherIsBetter(metric?.goal_criteria);

        let direction = 'flat';
        let tone = DELTA_TONE.NEUTRAL;
        let sign = '+';

        if (Math.abs(deltaPercent) >= 0.005) {
            if (deltaPercent > 0) {
                direction = 'up';
                sign = '+';
                tone = higherIsBetter ? DELTA_TONE.GOOD : DELTA_TONE.BAD;
            } else {
                direction = 'down';
                sign = '-';
                tone = higherIsBetter ? DELTA_TONE.BAD : DELTA_TONE.GOOD;
            }
        }

        return { percent: Math.abs(deltaPercent).toFixed(2), direction, tone, sign };
    },

    /**
     * Clear delta cache entries for a specific metric
     * Removes both regular and projected cache entries
     * 
     * @param {number|string} metricId - The metric ID to clear cache for
     */
    clearDeltaCacheForMetric(metricId) {
        const prefix = `${metricId}-`;
        const projectedPrefix = `projected-${metricId}-`;
        
        for (const key in this.deltaCache) {
            if (key.startsWith(prefix) || key.startsWith(projectedPrefix)) {
                delete this.deltaCache[key];
            }
        }
    },


    /**
     * Get delta information for a cell
     * Returns null if delta cannot be computed, or:
     * { percent: '12.34', direction: 'up'|'down'|'flat', tone: 'good'|'bad'|'neutral', sign: '+'|'-' }
     * 
     * @param {Object} row - Row data containing metric_id
     * @param {number} headerIdx - Index into cached_scorecard_headers array
     * @returns {Object|null}
     */
    getDeltaInfo(row, headerIdx) {
      const headers = this.cached_scorecard_headers;
      if (!headers || headerIdx < 0 || headerIdx >= headers.length) return null;
      
      const headerData = headers[headerIdx];
      if (!headerData || headerData.isPlaceholder) return null;

      const interval = this.activeInterval;
      if (interval !== headerData.interval) return null;

        
        const metricId = row.metric_id;
        const startDate = headerData.start_date;
        const cacheKey = `${metricId}-${interval}-${startDate}`;

        // Return cached result
        if (this.deltaCache[cacheKey] !== undefined) {
            return this.deltaCache[cacheKey];
        }
        
        const metric = this.fullScorecardData?.metrics?.[metricId];
        if (!metric) {
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        // Get current value
        const currentData = metric.data?.[interval]?.[startDate];
        if (!currentData) {
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        const currentValue = this.parseNumericValue(currentData.no_seperators_or_labels_formatted_value);
        if (currentValue === null) {
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        // Get previous period directly using index - O(1) instead of findIndex O(n)
        if (headerIdx <= 0) {
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        const previousHeader = headers[headerIdx - 1];
        if (!previousHeader || previousHeader.isPlaceholder) {
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        const previousDate = previousHeader.start_date;
        const previousData = metric.data?.[interval]?.[previousDate];
        if (!previousData) {
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        let previousValue = this.parseNumericValue(previousData.no_seperators_or_labels_formatted_value);

        const deltaPercent = this.calculateDelta(currentValue, previousValue)

        const result = this.buildDeltaResult(deltaPercent, metric);
        this.deltaCache[cacheKey] = result;
        return result;
    },

    /**
     * Get delta information for projected value vs previous period
     * Uses the projected value instead of current period actual value
     * 
     * @param {Object} row - Row data containing metric_id
     * @param {number} headerIdx - Index into cached_scorecard_headers array
     * @returns {Object|null}
     */
    getProjectedDeltaInfo(row, headerIdx) {
        const headers = this.cached_scorecard_headers;
        if (!headers || headerIdx < 0 || headerIdx >= headers.length) return null;

        const headerData = headers[headerIdx];
        if (!headerData || headerData.isPlaceholder) return null;

        const interval = this.activeInterval;
        if (interval !== headerData.interval) return null;

        const startDate = headerData.start_date;
        const metricId = row.metric_id;

        const cacheKey = `projected-${metricId}-${interval}-${startDate}`;

        // Return cached result
        if (this.deltaCache[cacheKey] !== undefined) {
            return this.deltaCache[cacheKey];
        }

        const projected = this.getCellProjectedRawValue(row, headerData)
        
        if (!projected && 0 !== projected) {
          // fallback to current
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        const projectedValue = this.parseNumericValue(projected);
        if (projectedValue === null) {
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        // Get previous period directly using index
        if (headerIdx <= 0) {
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        const previousHeader = headers[headerIdx - 1];
        if (!previousHeader || previousHeader.isPlaceholder) {
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        const metric = this.fullScorecardData?.metrics?.[metricId];
        if (!metric) {
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        const previousDate = previousHeader.start_date;
        const previousData = metric.data?.[interval]?.[previousDate];
        if (!previousData) {
            this.deltaCache[cacheKey] = null;
            return null;
        }
        
        const previousValue = this.parseNumericValue(previousData.no_seperators_or_labels_formatted_value);
        
        if (previousValue === null) {
            this.deltaCache[cacheKey] = null;
            return null;
        }

        const deltaPercent = this.calculateDelta(projectedValue, previousValue)

        const result = this.buildDeltaResult(deltaPercent, metric);
        this.deltaCache[cacheKey] = result;
        return result;
    },

    /**
     * Parse a value to numeric, handling formatted strings
     * @param {any} value 
     * @returns {number|null}
     */
    parseNumericValue(value) {
        if (value === null || value === undefined || value === '' || value === '-') {
            return null;
        }
        
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
    },

    /**
     * Get the complete cell content HTML including value and delta pill
     * This consolidates all delta logic into a single method call
     * 
     * @param {Object} row - Row data containing metric_id
     * @param {Object} headerData - Header data with start_date
     * @param {number} headerIdx - Index into cached_scorecard_headers array
     * @returns {string} HTML string with value and optional delta pill
     */
    getCellValueContent(row, headerData, headerIdx) {
        if (this.activeInterval !== headerData.interval) return '';
        // The theme mixin's sr-only "All-time high" / "All-time low" rides along with the value in
        // every theme, as the pill's tone label does below: weight alone is silent to a screen reader.
        const value = `${this.getCellFormattedValue(row, headerData)}${this.getCellRecordLabel(row, headerData)}`;

        if (!this.shouldShowDelta()) {
            return value;
        }

        const delta = this.getDeltaInfo(row, headerIdx);

        if (!delta) {
            return value;
        }

        return `${value}${this.renderDeltaPill(delta)}`;
    },

    /**
     * Get the complete cell content HTML for projected period column
     * Uses projected value and compares against previous period for delta
     * 
     * @param {Object} row - Row data containing metric_id
     * @param {Object} headerData - Header data with start_date
     * @param {number} headerIdx - Index into cached_scorecard_headers array
     * @returns {string} HTML string with projected value and optional delta pill
     */
    getCellProjectedValueContent(row, headerData, headerIdx) {
        if (this.activeInterval !== headerData.interval) return '';

        const value = this.getCellProjectedFormattedValue(row, headerData);   

        if (!this.shouldShowDelta()) {
            return value;
        }

        const delta = this.getProjectedDeltaInfo(row, headerIdx);

        if (!delta) {
            return value;
        }
        
        return `${value}${this.renderDeltaPill(delta)}`;
    },

    /**
     * Non-colour cue for the pill tone (WCAG 1.4.1). The arrow and sign report direction, so colour
     * is otherwise the only channel carrying good/bad; this rides along as sr-only text.
     *
     * @param {string} tone - one of DELTA_TONE
     * @returns {string}
     */
    getDeltaToneLabel(tone) {
        if (tone === DELTA_TONE.GOOD) return this.deltaToneLabels.good;
        if (tone === DELTA_TONE.BAD) return this.deltaToneLabels.bad;
        return '';
    },

    /**
     * Get the CSS classes for a delta pill: one for tone (colour) and one for direction (the arrow,
     * rendered via a CSS ::before pseudo-element). Keeping them separate is what lets a
     * lower-is-better metric show a down arrow in a green pill.
     *
     * @param {string} tone - one of DELTA_TONE
     * @param {string} direction - 'up' | 'down' | 'flat'
     * @returns {string} CSS class string
     */
    getDeltaPillClass(tone, direction) {
        const base = 'sc-delta-pill';
        const toneClass = tone === DELTA_TONE.GOOD ? 'sc-delta-good' : tone === DELTA_TONE.BAD ? 'sc-delta-bad' : 'sc-delta-neutral';
        const arrowClass = direction === 'up' ? 'sc-delta-up' : direction === 'down' ? 'sc-delta-down' : '';
        return arrowClass ? `${base} ${toneClass} ${arrowClass}` : `${base} ${toneClass}`;
    },

    /**
     * The delta pill markup. Both the actual and projected cell builders render it identically, so
     * it lives here rather than being kept in lockstep in two places.
     *
     * @param {Object} delta - a buildDeltaResult() descriptor
     * @returns {string} HTML string
     */
    renderDeltaPill(delta) {
        const pillClass = this.getDeltaPillClass(delta.tone, delta.direction);
        const pillText = delta.direction === 'flat' ? '+0.00%' : `${delta.sign}${delta.percent}%`;
        return `<span class="${pillClass}">${pillText}<span class="sr-only"> ${this.getDeltaToneLabel(delta.tone)}</span></span>`;
    }
});
